/*
  Photo gallery ("Shoobies in the wild"): the three looks of sections/horizontal-parallax-gallery.liquid and their
  shared full-screen viewer. Spec: docs/superpowers/specs/2026-10-15-photo-gallery-buybox-design.md
  - Course strip: an endless drift that sleeps when nothing can move; depth, centre focus, counter, progress line, scene drift.
  - Photo band: rows repeated to fill the screen, then looped; paused by hover, focus, touch, the button or off screen.
  - Photo deck: a fanned stack; the top photo is thrown by a drag, a swipe, the arrows or the keys.
  Every root ([data-pg]) is set up once, and torn down on the theme editor's section unload.
*/
(function () {
  'use strict';
  if (window.ShoobiePhotoGallery) return;

  var motion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var still = function () { return !!(motion && motion.matches); };
  var pad = function (n) { return (n < 10 ? '0' : '') + n; };
  var live = new Map();   // root -> stop()

  function listen(bin, el, type, fn, opts) { el.addEventListener(type, fn, opts); bin.push(function () { el.removeEventListener(type, fn, opts); }); }
  function setText(el, s) { if (el && el.textContent !== s) el.textContent = s; }
  function index(el) { return parseInt(el.getAttribute('data-pg-i'), 10) || 0; }
  function visibleFocus(el) { try { return el.matches(':focus-visible'); } catch (err) { return true; } }
  function modified(e) { return e.altKey || e.ctrlKey || e.metaKey || e.shiftKey; }   // Alt/Ctrl/Cmd/Shift + arrow: the browser's, not ours
  // A copy of a photo for the loops: hidden from assistive tech and the tab order, and without the theme editor's
  // block attributes, so the editor keeps pointing at the original.
  function cloneFor(el) {
    var c = el.cloneNode(true);
    c.setAttribute('data-pg-copy', ''); c.setAttribute('aria-hidden', 'true'); c.setAttribute('tabindex', '-1');
    c.classList.remove('is-focus');
    [].slice.call(c.attributes).forEach(function (a) { if (a.name.indexOf('data-shopify-editor') === 0) c.removeAttribute(a.name); });
    return c;
  }

  // ---- Full-screen viewer: a modal <dialog>, so it sits above the header and any transformed parent ----
  function viewer(root, photos, bin) {
    var dlg = root.querySelector('[data-pg-viewer]');
    if (!dlg || typeof dlg.showModal !== 'function') return function () {};
    var img = dlg.querySelector('[data-pg-viewer-img]'), cap = dlg.querySelector('[data-pg-viewer-cap]');
    var link = dlg.querySelector('[data-pg-viewer-link]'), count = dlg.querySelector('[data-pg-viewer-count]');
    var at = 0, opener = null, startX = null, overflow = '';
    function show(n) {
      at = (n + photos.length) % photos.length;
      var p = photos[at];
      img.removeAttribute('src');
      img.setAttribute('sizes', '100vw');
      img.setAttribute('srcset', p.getAttribute('data-pg-srcset') || '');
      img.setAttribute('src', p.getAttribute('data-pg-src') || '');
      img.setAttribute('alt', p.getAttribute('data-pg-alt') || '');
      var c = p.getAttribute('data-pg-caption'), l = p.getAttribute('data-pg-link');
      cap.textContent = c || ''; cap.hidden = !c;
      if (l) { link.setAttribute('href', l); link.hidden = false; } else { link.removeAttribute('href'); link.hidden = true; }
      setText(count, pad(at + 1) + ' / ' + pad(photos.length));
    }
    function restore() { document.documentElement.style.overflow = overflow; }
    listen(bin, dlg, 'close', function () {
      restore();
      if (opener && opener.isConnected) opener.focus({ preventScroll: true });
      opener = null;
    });
    listen(bin, dlg, 'click', function (e) {
      if (e.target.closest('[data-pg-close]')) dlg.close();
      else if (e.target.closest('[data-pg-vprev]')) show(at - 1);
      else if (e.target.closest('[data-pg-vnext]')) show(at + 1);
    });
    listen(bin, dlg, 'keydown', function (e) {
      if (modified(e)) return;
      if (e.key === 'ArrowLeft') { show(at - 1); e.preventDefault(); }
      else if (e.key === 'ArrowRight') { show(at + 1); e.preventDefault(); }
    });
    listen(bin, dlg, 'pointerdown', function (e) { startX = e.clientX; });
    listen(bin, dlg, 'pointerup', function (e) {
      // A mouse drag ending on a button is a click; a touch swipe that starts on an arrow (it sits over the photo on phones) is a swipe.
      if (startX !== null && Math.abs(e.clientX - startX) > 50 && (e.pointerType !== 'mouse' || !e.target.closest('button, a'))) show(at + (e.clientX < startX ? 1 : -1));
      startX = null;
    });
    bin.push(function () { if (dlg.open) { dlg.close(); restore(); } });
    return function open(n, from) {
      opener = from || null;
      show(n);
      overflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      dlg.showModal();
      dlg.querySelector('[data-pg-close]').focus();
    };
  }

  // ---- Course strip (revision 1): endless, overlapping, drifting on its own ----
  // One copy of the photos goes before the originals and one after, so photos fill both edges. The strip drifts
  // left by itself and wraps by one set's width, where the view looks the same. Photos fan apart near the middle.
  var DRIFT = 30;   // px per second
  function strip(root, photos, open, bin) {
    var track = root.querySelector('[data-pg-track]'), viewerEl = root.querySelector('[data-pg-viewer]');
    var prev = root.querySelector('[data-pg-prev]'), next = root.querySelector('[data-pg-next]'), button = root.querySelector('[data-pg-pause]');
    var count = root.querySelector('[data-pg-count-out]'), bar = root.querySelector('[data-pg-bar]'), scene = root.querySelector('[data-pg-scene]');
    var drift = Math.min(100, Math.max(0, parseFloat(root.getAttribute('data-pg-drift')) || 0)) / 100;
    var items = photos.slice(), loop = false, setW = 0, sets = 0, lastW = 0, frame = 0, raf = 0, settle = 0, resizeTimer = 0, liveTimer = 0, last = 0, carry = 0;
    var held = false, hover = false, away = false, kbd = false, quietUntil = 0, pending = -1, wakeTimer = 0;
    function now() { return window.performance ? performance.now() : Date.now(); }
    function quiet(ms) { quietUntil = Math.max(quietUntil, now() + ms); }
    function mid(el) { return el.offsetLeft + el.offsetWidth / 2; }
    function clearCopies() { [].forEach.call(track.querySelectorAll('[data-pg-copy]'), function (c) { c.remove(); }); }
    function build() {
      clearCopies();
      items = photos.slice(); loop = false; setW = 0; sets = 0; track.scrollLeft = 0; lastW = track.clientWidth;
      if (photos.length > 1) {   // a single photo stays still; anything more loops
        var after = photos.map(cloneFor), before = [], s;
        after.forEach(function (c) { track.appendChild(c); });
        setW = Math.max(1, after[0].offsetLeft - photos[0].offsetLeft);
        // Enough copies on each side to cover the screen (a few photos, or a very wide screen, need more), and two sets
        // at least, so a hard fling on a phone never reaches the end.
        sets = Math.max(2, Math.ceil(track.clientWidth / setW));
        for (s = 1; s < sets; s++) photos.forEach(function (el) { var c = cloneFor(el); track.appendChild(c); after.push(c); });
        for (s = 0; s < sets; s++) photos.forEach(function (el) { var c = cloneFor(el); track.insertBefore(c, photos[0]); before.push(c); });
        loop = true; items = before.concat(photos, after); track.scrollLeft = sets * setW;
      }
      track.classList.toggle('is-static', !loop);
      if (button) button.hidden = !loop || still();
      // While it drifts the counter changes by itself, so it only speaks for steps someone takes (see go()).
      if (count) count.setAttribute('aria-live', loop ? 'off' : 'polite');
      update();
      wake();
    }
    function nearest() {
      var c = track.scrollLeft + track.clientWidth / 2, best = 0, gap = Infinity;
      items.forEach(function (p, k) { var d = Math.abs(mid(p) - c); if (d < gap) { gap = d; best = k; } });
      return best;
    }
    function update() {
      frame = 0;
      var w = track.clientWidth || 1, c = track.scrollLeft + w / 2, calm = still(), max = track.scrollWidth - w;
      var centres = items.map(mid), n = 0, gap = Infinity;   // read every position first, then write
      centres.forEach(function (m, k) { var g = Math.abs(m - c); if (g < gap) { gap = g; n = k; } });
      var progress = loop ? ((((track.scrollLeft - sets * setW) % setW) + setW) % setW) / setW : (max > 0 ? Math.min(1, track.scrollLeft / max) : 1);
      var reach = w / 2 + 400;   // px from the centre: anything nearer may show this frame
      items.forEach(function (p, k) {
        // Far off screen: its last values stand, and it is written again before it can come into view.
        if (!calm && Math.abs(centres[k] - c) > reach && !p.classList.contains('is-focus')) return;
        var d = (centres[k] - c) / w, depth = parseFloat(p.getAttribute('data-pg-depth')) || 1;
        // Depth drift, plus a spread that opens neighbours near the middle and draws them together toward the edges
        // (a·(1 − 4a²) rises in the middle, falls back to 0 at the edges, and nets out across the screen).
        var a = Math.max(-0.5, Math.min(0.5, d)), shift = d * (depth - 1) * -300 + 0.13 * w * a * (1 - 4 * a * a);
        p.style.setProperty('--pg-shift', calm ? '0px' : shift.toFixed(1) + 'px');
        p.style.setProperty('--pg-focus', calm ? (k === n ? '1' : '0') : Math.max(0, 1 - Math.abs(d) * 2.4).toFixed(3));
        p.classList.toggle('is-focus', k === n);
      });
      setText(count, pad(index(items[n]) + 1) + ' / ' + pad(photos.length));
      if (bar) bar.style.transform = 'scaleX(' + Math.max(0.04, progress).toFixed(3) + ')';
      if (loop) {
        if (prev) prev.disabled = false;
        if (next) next.disabled = false;
      } else {
        // By photo, not scroll position. A button about to be disabled hands its focus on.
        var atStart = n === 0, atEnd = n === photos.length - 1, active = document.activeElement;
        if (next && atEnd && active === next) (prev && !atStart ? prev : photos[n]).focus({ preventScroll: true });
        if (prev && atStart && active === prev) (next && !atEnd ? next : photos[n]).focus({ preventScroll: true });
        if (prev) prev.disabled = atStart;
        if (next) next.disabled = atEnd;
      }
      // The scene goes out and back once per loop, so the seam where the strip wraps doesn't show.
      var wave = (1 - Math.cos(2 * Math.PI * progress)) / 2;
      if (scene) scene.style.transform = calm ? '' : 'translateX(' + (-wave * 0.15 * drift * root.clientWidth).toFixed(1) + 'px)';
    }
    function wrap() {
      if (!loop || kbd) return;
      if (track.scrollLeft >= setW * (sets + 1)) track.scrollLeft -= setW;
      else if (track.scrollLeft < setW * (sets - 0.5)) track.scrollLeft += setW;
    }
    function go(dir) {
      quiet(4000);
      if (count && loop) {
        count.setAttribute('aria-live', 'polite');
        clearTimeout(liveTimer);
        liveTimer = setTimeout(function () { count.setAttribute('aria-live', 'off'); }, 1500);
      }
      var at = photos.indexOf(document.activeElement), target;
      if (at > -1) {
        pending = -1;
        target = photos[loop ? (at + dir + photos.length) % photos.length : Math.max(0, Math.min(photos.length - 1, at + dir))];
        if (loop) {   // jump a set to the same-looking view nearest the target, so the glide stays short
          var to = mid(target) - track.clientWidth / 2, from = track.scrollLeft;
          if (to - from > setW / 2) track.scrollLeft = from + setW;
          else if (from - to > setW / 2) track.scrollLeft = from - setW;
        }
        target.focus({ preventScroll: true });
      } else {
        // Clicks during a glide count from where the glide is going, not from where it is now.
        var k = Math.max(0, Math.min(items.length - 1, (pending > -1 ? pending : nearest()) + dir));
        target = items[k]; pending = k;
      }
      track.scrollTo({ left: mid(target) - track.clientWidth / 2, behavior: still() ? 'auto' : 'smooth' });
    }
    // The drift runs only while something can move. Each thing that holds it (pause, hover, keyboard, the viewer,
    // off screen) wakes it when it lets go; a quiet spell after a touch or a step wakes it with a timer.
    function blocked() { return !loop || still() || held || hover || away || kbd || !!(viewerEl && viewerEl.open); }
    function wake() {
      if (raf || blocked()) return;
      var wait = quietUntil - now();
      if (wait > 0) { clearTimeout(wakeTimer); wakeTimer = setTimeout(wake, wait + 20); return; }
      last = 0; raf = requestAnimationFrame(tick);
    }
    function tick(t) {
      raf = 0;
      if (blocked() || now() < quietUntil) { carry = 0; wake(); return; }
      raf = requestAnimationFrame(tick);
      var dt = last ? Math.min(64, t - last) : 0; last = t;
      carry += DRIFT * dt / 1000;   // scrollLeft takes whole pixels
      var step = Math.floor(carry);
      if (step) {
        carry -= step; track.scrollLeft += step;
        if (track.scrollLeft >= setW * (sets + 1)) track.scrollLeft -= setW;
        // Paint this frame with fresh positions: across the wrap, the photos now in view are their twins from a set away.
        if (frame) { cancelAnimationFrame(frame); frame = 0; }
        update();
      }
    }
    function syncButton() {
      if (!button) return;
      button.setAttribute('aria-pressed', held ? 'true' : 'false');   // the label stays "Pause the photos"
    }
    listen(bin, track, 'scroll', function () {
      if (!frame) frame = requestAnimationFrame(update);
      clearTimeout(settle); settle = setTimeout(function () { pending = -1; wrap(); }, 160);
    }, { passive: true });
    if (prev) listen(bin, prev, 'click', function () { go(-1); });
    if (next) listen(bin, next, 'click', function () { go(1); });
    if (button) listen(bin, button, 'click', function () { held = !held; syncButton(); wake(); });
    if (viewerEl) listen(bin, viewerEl, 'close', wake);
    listen(bin, root, 'keydown', function (e) {
      if (e.target.closest('[data-pg-viewer]') || modified(e)) return;
      if (e.key === 'ArrowLeft') { go(-1); e.preventDefault(); }
      else if (e.key === 'ArrowRight') { go(1); e.preventDefault(); }
    });
    listen(bin, track, 'click', function (e) {
      var p = e.target.closest('[data-pg-i]');
      if (!p) return;
      var i = index(p);
      open(i, p.hasAttribute('data-pg-copy') ? photos[i] : p);
    });
    listen(bin, track, 'pointerenter', function (e) { if (e.pointerType === 'mouse') hover = true; });
    listen(bin, track, 'pointerleave', function (e) { if (e.pointerType === 'mouse') { hover = false; wake(); } });
    listen(bin, track, 'pointerdown', function (e) { if (e.pointerType !== 'mouse') quiet(4000); });
    listen(bin, track, 'touchmove', function () { quiet(4000); }, { passive: true });
    listen(bin, track, 'wheel', function () { quiet(3000); }, { passive: true });
    listen(bin, track, 'focusin', function (e) {
      kbd = visibleFocus(e.target);
      if (!kbd) return;
      // Focus can come back to an original that sits off screen (after the viewer opened from a copy).
      var r = e.target.getBoundingClientRect(), tr = track.getBoundingClientRect();
      if (r.left < tr.left || r.right > tr.right) track.scrollLeft += (r.left + r.width / 2) - (tr.left + tr.width / 2);
    });
    listen(bin, track, 'focusout', function (e) {
      if (e.relatedTarget && track.contains(e.relatedTarget)) return;
      if (kbd) quiet(2000);
      kbd = false;
      wake();
    });
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) { away = !entries[entries.length - 1].isIntersecting; wake(); });
      io.observe(root);
      bin.push(function () { io.disconnect(); });
    }
    listen(bin, window, 'resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        if (track.clientWidth === lastW) return;   // only the height changed (a phone's toolbar): nothing to rebuild
        var r = loop ? (track.scrollLeft - sets * setW) / setW : 0;
        build();
        if (loop) track.scrollLeft = sets * setW + r * setW;
      }, 200);
    });
    bin.push(function () {
      cancelAnimationFrame(raf); raf = 0; if (frame) cancelAnimationFrame(frame); clearTimeout(settle); clearTimeout(resizeTimer); clearTimeout(liveTimer); clearTimeout(wakeTimer);
      if (count) count.setAttribute('aria-live', 'polite');
      clearCopies(); track.scrollLeft = 0; track.classList.remove('is-static');
      photos.forEach(function (p) { p.classList.remove('is-focus'); p.style.removeProperty('--pg-shift'); p.style.removeProperty('--pg-focus'); });
      if (scene) scene.style.transform = '';
      if (button) button.hidden = false;
    });
    build();
    syncButton();
  }

  // ---- Photo band ----
  function band(root, photos, open, bin) {
    var rows = [].slice.call(root.querySelectorAll('[data-pg-row]'));
    var button = root.querySelector('[data-pg-pause]');
    var held = false, away = false, lastW = 0, resizeTimer = 0;
    function clearCopies() { [].forEach.call(root.querySelectorAll('[data-pg-copy]'), function (c) { c.remove(); }); }
    function build() {
      clearCopies();
      lastW = root.clientWidth;
      rows.forEach(function (row, r) {
        var track = row.querySelector('[data-pg-track]'), originals = [].slice.call(track.children);
        track.style.removeProperty('--pg-dur');
        if (still() || !originals.length) return;
        var one = track.offsetWidth;   // one set: each card carries its gap as margin-right
        if (one < 40) return;
        var sets = Math.max(1, Math.ceil(row.clientWidth / one));
        for (var s = 1; s < sets * 2; s++) originals.forEach(function (el) { track.appendChild(cloneFor(el)); });
        track.style.setProperty('--pg-dur', (one * sets / (r ? 32 : 40)).toFixed(2) + 's');
      });
    }
    function sync() {
      root.classList.toggle('is-paused', held || away);
      if (button) button.setAttribute('aria-pressed', held ? 'true' : 'false');   // the label stays "Pause the photos"
    }
    if (button) listen(bin, button, 'click', function () { held = !held; sync(); });
    // Keyboard focus on an original photo: stop the row at its start (.is-kbd sets animation: none) and scroll the
    // photo into view; drifted, it could sit off to the left where no scrolling reaches. A mouse return of focus
    // (closing the viewer by click) is not :focus-visible, so the row keeps moving.
    rows.forEach(function (row) {
      listen(bin, row, 'focusin', function (e) {
        var card = e.target.closest('[data-pg-i]');
        if (!card || card.hasAttribute('data-pg-copy') || !visibleFocus(card)) return;
        row.classList.add('is-kbd');
        var r = card.getBoundingClientRect(), rr = row.getBoundingClientRect();
        row.scrollLeft += (r.left + r.width / 2) - (rr.left + rr.width / 2);
      });
      listen(bin, row, 'focusout', function (e) {
        if (e.relatedTarget && row.contains(e.relatedTarget)) return;
        row.classList.remove('is-kbd'); row.scrollLeft = 0;
      });
      bin.push(function () { row.classList.remove('is-kbd'); row.scrollLeft = 0; });
    });
    rows.forEach(function (row) {
      var t = 0;
      var down = function (e) { if (e.pointerType !== 'mouse') { clearTimeout(t); row.classList.add('is-held'); } };
      var up = function (e) { if (e.pointerType !== 'mouse') { clearTimeout(t); t = setTimeout(function () { row.classList.remove('is-held'); }, 2500); } };
      listen(bin, row, 'pointerdown', down);
      listen(bin, row, 'pointerup', up);
      listen(bin, row, 'pointercancel', up);
      bin.push(function () { clearTimeout(t); row.classList.remove('is-held'); });
    });
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) { away = !entries[entries.length - 1].isIntersecting; sync(); });
      io.observe(root);
      bin.push(function () { io.disconnect(); });
    }
    listen(bin, root, 'click', function (e) {
      if (e.target.closest('[data-pg-viewer]')) return;
      var p = e.target.closest('[data-pg-i]');
      if (p) { var i = index(p); open(i, p.hasAttribute('data-pg-copy') ? photos[i] : p); }
    });
    listen(bin, window, 'resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { if (Math.abs(root.clientWidth - lastW) > 1) build(); }, 200);
    });
    bin.push(function () {
      clearTimeout(resizeTimer); clearCopies(); root.classList.remove('is-paused');
      rows.forEach(function (row) { row.querySelector('[data-pg-track]').style.removeProperty('--pg-dur'); });
    });
    build();
    sync();
  }

  // ---- Photo deck ----
  var FAN = [[0, 0, 0, 1], [40, -8, 6, 0.94], [-36, -14, -7, 0.9], [14, -28, 3, 0.86]];
  function deck(root, photos, open, bin) {
    var stack = root.querySelector('[data-pg-track]');
    var prev = root.querySelector('[data-pg-prev]'), next = root.querySelector('[data-pg-next]');
    var count = root.querySelector('[data-pg-count-out]'), layers = root.querySelectorAll('.pg-deck__layer');
    var order = photos.map(function (p, k) { return k; }), front = 0, busy = false, timer = 0, drag = null, dragged = false, queued = 0;
    function fling(side) { return 'translate(' + side * 520 + 'px, 40px) rotate(' + side * 22 + 'deg)'; }
    function place(card, k, transform) {
      var f = FAN[Math.min(k, 3)];
      card.style.zIndex = String(Math.max(1, 20 - k));
      card.style.opacity = k > 3 ? '0' : '1';
      card.style.transform = transform || 'translate(' + f[0] + 'px, ' + f[1] + 'px) rotate(' + f[2] + 'deg) scale(' + f[3] + ')';
      card.tabIndex = k === 0 ? 0 : -1;
      card.setAttribute('aria-hidden', k === 0 ? 'false' : 'true');
    }
    function meta() {
      var top = photos[order[0]];
      setText(count, pad(order[0] + 1) + ' / ' + pad(photos.length));
      if (layers.length === 2) {
        var url = 'url("' + top.getAttribute('data-pg-bg') + '")';
        if (layers[front].style.backgroundImage !== url) {
          var other = layers[1 - front];
          other.style.backgroundImage = url; other.classList.add('is-on'); layers[front].classList.remove('is-on'); front = 1 - front;
        }
      }
    }
    function layout() { order.forEach(function (idx, k) { place(photos[idx], k); }); meta(); }
    function step(dir, side) {
      if (photos.length < 2) return;
      if (busy) { queued = Math.max(-3, Math.min(3, queued + dir)); return; }   // clicks during a throw wait their turn
      var hadFocus = stack.contains(document.activeElement);
      if (dir > 0) {
        var top = photos[order[0]];
        order.push(order.shift());
        order.forEach(function (idx, k) { if (photos[idx] !== top) place(photos[idx], k); });
        meta();
        if (still()) {
          place(top, order.length - 1);
        } else {
          busy = true;
          place(top, 0, fling(side || -1));
          top.style.opacity = '0'; top.style.zIndex = '30'; top.tabIndex = -1; top.setAttribute('aria-hidden', 'true');
          timer = setTimeout(function () {
            top.classList.add('is-instant'); place(top, order.length - 1); void top.offsetWidth; top.classList.remove('is-instant');
            busy = false;
            // Back steps are instant, so all of them run now; a forward step throws again and its own timer takes the next.
            while (queued < 0) { queued++; step(-1); }
            if (queued > 0) { queued--; step(1); }
          }, 380);
        }
      } else {
        var last = photos[order[order.length - 1]];
        order.unshift(order.pop());
        if (!still()) {
          last.classList.add('is-instant'); place(last, 0, fling(-1)); last.style.opacity = '0';
          void last.offsetWidth; last.classList.remove('is-instant');
        }
        layout();
      }
      if (hadFocus) photos[order[0]].focus({ preventScroll: true });
    }
    if (prev) listen(bin, prev, 'click', function () { step(-1); });
    if (next) listen(bin, next, 'click', function () { step(1); });
    listen(bin, root, 'keydown', function (e) {
      if (e.target.closest('[data-pg-viewer]') || modified(e)) return;
      if (e.key === 'ArrowLeft') { step(-1); e.preventDefault(); }
      else if (e.key === 'ArrowRight') { step(1); e.preventDefault(); }
    });
    listen(bin, stack, 'pointerdown', function (e) {
      dragged = false;
      var card = e.target.closest('[data-pg-i]');
      if (!card || card !== photos[order[0]] || busy || (e.pointerType === 'mouse' && e.button !== 0)) return;
      drag = { card: card, x: e.clientX, dx: 0, id: e.pointerId };
      card.classList.add('is-drag');
      if (card.setPointerCapture) { try { card.setPointerCapture(e.pointerId); } catch (err) { /* capture is optional */ } }
    });
    listen(bin, stack, 'pointermove', function (e) {
      if (!drag || e.pointerId !== drag.id) return;
      drag.dx = e.clientX - drag.x;
      if (Math.abs(drag.dx) > 6) dragged = true;
      drag.card.style.transform = 'translate(' + drag.dx + 'px, ' + (Math.abs(drag.dx) * 0.06).toFixed(1) + 'px) rotate(' + (drag.dx / 18).toFixed(2) + 'deg)';
    });
    function release(e) {
      if (!drag || e.pointerId !== drag.id) return;
      var d = drag; drag = null; d.card.classList.remove('is-drag');
      if (Math.abs(d.dx) > 90) step(1, d.dx < 0 ? -1 : 1); else place(d.card, 0);
    }
    listen(bin, stack, 'pointerup', release);
    listen(bin, stack, 'pointercancel', release);
    listen(bin, stack, 'click', function (e) {
      var card = e.target.closest('[data-pg-i]');
      if (!card) return;
      if (dragged) { dragged = false; e.preventDefault(); return; }
      if (card === photos[order[0]]) open(index(card), card);
    });
    bin.push(function () {
      clearTimeout(timer); queued = 0;
      photos.forEach(function (p) {
        p.style.removeProperty('z-index'); p.style.removeProperty('opacity'); p.style.removeProperty('transform');
        p.classList.remove('is-drag', 'is-instant'); p.removeAttribute('tabindex'); p.removeAttribute('aria-hidden');
      });
    });
    layout();
  }

  // ---- Set-up and tear-down ----
  function init(root) {
    if (live.has(root)) return;
    var photos = [].slice.call(root.querySelectorAll('[data-pg-i]:not([data-pg-copy])'));
    if (!photos.length) return;
    photos.sort(function (a, b) { return index(a) - index(b); });
    var bin = [], look = root.getAttribute('data-pg-look');
    var open = viewer(root, photos, bin);
    (look === 'band' ? band : look === 'deck' ? deck : strip)(root, photos, open, bin);
    root.setAttribute('data-pg-ready', '');
    live.set(root, function () {
      while (bin.length) bin.pop()();
      root.removeAttribute('data-pg-ready');
      live.delete(root);
    });
  }
  function initAll(scope) { [].forEach.call((scope || document).querySelectorAll('[data-pg]'), init); }
  function stopIn(scope) {
    var stops = [];
    live.forEach(function (stop, root) { if (!scope || scope === root || scope.contains(root)) stops.push(stop); });
    stops.forEach(function (stop) { stop(); });
  }

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { stopIn(e.target); });
  if (motion && motion.addEventListener) {
    motion.addEventListener('change', function () {
      var roots = []; live.forEach(function (stop, root) { roots.push(root); });
      stopIn(); roots.forEach(init);
    });
  }
  window.ShoobiePhotoGallery = { init: initAll, stop: stopIn };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(); });
  else initAll();
})();
